import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Input,
  IconButton,
  List,
  ListItem,
  ListItemContent,
  ListItemDecorator,
  Modal,
  ModalDialog,
  ModalClose,
  Alert,
  Chip,
  CircularProgress,
  Divider,
  Switch,
  Tooltip,
} from '@mui/joy';
import {
  Plus,
  PencilSimple as Edit2,
  Trash as Trash2,
  FloppyDisk as Save,
  X,
  DotsSixVertical as GripVertical,
  CaretRight as ChevronRight,
  Layout,
  Gear as Settings,
  CloudLightning,
} from '@phosphor-icons/react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { DraggablePortal } from '../../common/DraggablePortal';
import { useNotification } from '../../../contexts/NotificationContext';
import api from '../../../lib/api';

interface Stage {
  id: number;
  name: string;
  order: number;
  board: number;
  storm_watchlist_enabled?: boolean;
}

interface Board {
  id: number;
  name: string;
  is_default?: boolean;
  created_at?: string;
  updated_at?: string;
}

const OpportunityBoardSettings: React.FC = () => {
  const { showSuccess, showError } = useNotification();
  const [selectedBoard, setSelectedBoard] = useState<Board | null>(null);
  const [boards, setBoards] = useState<Board[]>([]);
  const [stages, setStages] = useState<Stage[]>([]);
  const [loadingBoards, setLoadingBoards] = useState(true);
  const [loadingStages, setLoadingStages] = useState(false);
  
  // Edit states
  const [editingBoard, setEditingBoard] = useState<Board | null>(null);
  const [editingStage, setEditingStage] = useState<Stage | null>(null);
  const [newBoardName, setNewBoardName] = useState('');
  const [newStageName, setNewStageName] = useState('');
  
  // Modal states
  const [showNewBoardModal, setShowNewBoardModal] = useState(false);
  const [showNewStageModal, setShowNewStageModal] = useState(false);
  const [showDeleteBoardModal, setShowDeleteBoardModal] = useState(false);
  const [showDeleteStageModal, setShowDeleteStageModal] = useState(false);
  const [stageToDelete, setStageToDelete] = useState<Stage | null>(null);

  // Fetch boards on mount
  useEffect(() => {
    fetchBoards();
  }, []);

  // Fetch stages when board is selected
  useEffect(() => {
    if (selectedBoard) {
      fetchStages(selectedBoard.id);
    }
  }, [selectedBoard]);

  const fetchBoards = async () => {
    try {
      setLoadingBoards(true);
      const response = await api.get<Board[] | { results: Board[] }>('/api/crm/opportunity-boards/');
      const boardsData = Array.isArray(response.data) ? response.data : (response.data as any).results || [];
      setBoards(boardsData);
      
      // Select the first board by default
      if (boardsData.length > 0 && !selectedBoard) {
        setSelectedBoard(boardsData[0]);
      }
    } catch (error) {
      showError('Failed to load opportunity boards');
    } finally {
      setLoadingBoards(false);
    }
  };

  const fetchStages = async (boardId: number) => {
    try {
      setLoadingStages(true);
      const response = await api.get<Stage[] | { results: Stage[] }>(`/api/crm/opportunity-board-statuses/?board=${boardId}`);
      const stagesData = Array.isArray(response.data) ? response.data : (response.data as any).results || [];
      // Sort by order
      const sortedStages = stagesData.sort((a: Stage, b: Stage) => (a.order || 0) - (b.order || 0));
      setStages(sortedStages);
    } catch (error) {
      showError('Failed to load stages');
    } finally {
      setLoadingStages(false);
    }
  };

  const handleCreateBoard = async () => {
    if (!newBoardName.trim()) {
      showError('Board name is required');
      return;
    }

    try {
      const response = await api.post<Board>('/api/crm/opportunity-boards/', {
        name: newBoardName.trim(),
        color: '#3b82f6', // Default blue color (required field)
        // organization is automatically set by backend from authenticated user
      });
      
      const newBoard = response.data;
      setBoards([...boards, newBoard]);
      setSelectedBoard(newBoard);
      setNewBoardName('');
      setShowNewBoardModal(false);
      showSuccess('Board created successfully');
    } catch (error: any) {
      // Check if it's a usage limit error
      if (error.response?.data?.upgrade_required) {
        console.log('Opportunity board usage limit exceeded - upgrade prompt will be shown');
        // The upgrade prompt will be shown automatically by the API interceptor
      } else {
        showError('Failed to create board');
      }
    }
  };

  const handleUpdateBoard = async (board: Board) => {
    if (!editingBoard || !editingBoard.name.trim()) {
      showError('Board name is required');
      return;
    }

    try {
      const response = await api.patch<Board>(`/api/crm/opportunity-boards/${board.id}/`, {
        name: editingBoard.name.trim(),
      });
      
      const updatedBoard = response.data;
      setBoards(boards.map(b => b.id === board.id ? updatedBoard : b));
      if (selectedBoard?.id === board.id) {
        setSelectedBoard(updatedBoard);
      }
      setEditingBoard(null);
      showSuccess('Board updated successfully');
    } catch (error) {
      showError('Failed to update board');
    }
  };

  const handleDeleteBoard = async () => {
    if (!selectedBoard) return;

    try {
      await api.delete(`/api/crm/opportunity-boards/${selectedBoard.id}/`);
      
      const remainingBoards = boards.filter(b => b.id !== selectedBoard.id);
      setBoards(remainingBoards);
      setSelectedBoard(remainingBoards.length > 0 ? remainingBoards[0] : null);
      setStages([]);
      setShowDeleteBoardModal(false);
      showSuccess('Board deleted successfully');
    } catch (error) {
      showError('Failed to delete board. It may be in use.');
    }
  };

  const handleCreateStage = async () => {
    if (!newStageName.trim() || !selectedBoard) {
      showError('Stage name is required');
      return;
    }

    try {
      const maxOrder = Math.max(...stages.map(s => s.order || 0), 0);
      const response = await api.post<Stage>('/api/crm/opportunity-board-statuses/', {
        name: newStageName.trim(),
        board: selectedBoard.id,
        order: maxOrder + 1,
      });
      
      const newStage = response.data;
      setStages([...stages, newStage]);
      setNewStageName('');
      setShowNewStageModal(false);
      showSuccess('Stage created successfully');
    } catch (error) {
      showError('Failed to create stage');
    }
  };

  const handleUpdateStage = async (stage: Stage) => {
    if (!editingStage || !editingStage.name.trim()) {
      showError('Stage name is required');
      return;
    }

    try {
      const response = await api.patch<Stage>(`/api/crm/opportunity-board-statuses/${stage.id}/`, {
        name: editingStage.name.trim(),
      });
      
      const updatedStage = response.data;
      setStages(stages.map(s => s.id === stage.id ? updatedStage : s));
      setEditingStage(null);
      showSuccess('Stage updated successfully');
    } catch (error) {
      showError('Failed to update stage');
    }
  };

  const handleToggleStormWatchlist = async (stage: Stage, enabled: boolean) => {
    // Optimistic update so the Switch reflects the user's intent immediately.
    setStages(stages.map(s => s.id === stage.id ? { ...s, storm_watchlist_enabled: enabled } : s));
    try {
      await api.patch<Stage>(`/api/crm/opportunity-board-statuses/${stage.id}/`, {
        storm_watchlist_enabled: enabled,
      });
    } catch (error) {
      setStages(stages.map(s => s.id === stage.id ? { ...s, storm_watchlist_enabled: !enabled } : s));
      showError('Failed to update Storm Tracker setting');
    }
  };

  const handleDeleteStage = async () => {
    if (!stageToDelete) return;

    try {
      await api.delete(`/api/crm/opportunity-board-statuses/${stageToDelete.id}/`);
      
      setStages(stages.filter(s => s.id !== stageToDelete.id));
      setStageToDelete(null);
      setShowDeleteStageModal(false);
      showSuccess('Stage deleted successfully');
    } catch (error) {
      showError('Failed to delete stage. It may have opportunities assigned.');
    }
  };

  const handleDragEnd = async (result: any) => {
    if (!result.destination) return;

    const items = Array.from(stages);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update order values
    const updatedStages = items.map((stage, index) => ({
      ...stage,
      order: index,
    }));

    setStages(updatedStages);

    // Update order on backend
    try {
      // Send batch update for all stages with new order
      await Promise.all(
        updatedStages.map(stage =>
          api.patch(`/api/crm/opportunity-board-statuses/${stage.id}/`, {
            order: stage.order,
          })
        )
      );
      showSuccess('Stage order updated successfully');
    } catch (error) {
      showError('Failed to update stage order');
      // Revert on error
      if (selectedBoard) {
        fetchStages(selectedBoard.id);
      }
    }
  };

  if (loadingBoards) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', gap: 3, height: 'calc(100vh - 200px)' }}>
      {/* Boards List */}
      <Card variant="outlined" sx={{ width: 350, overflow: 'auto' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography level="title-md" startDecorator={<Layout size={20} />}>
              Opportunity Boards
            </Typography>
            <Button
              size="sm"
              startDecorator={<Plus size={16} />}
              onClick={() => setShowNewBoardModal(true)}
            >
              New Board
            </Button>
          </Box>

          <List>
            {boards.map(board => (
              <ListItem
                key={board.id}
                sx={{
                  cursor: 'pointer',
                  bgcolor: selectedBoard?.id === board.id ? 'primary.softBg' : 'transparent',
                  borderRadius: 'sm',
                  mb: 0.5,
                }}
                onClick={() => setSelectedBoard(board)}
              >
                {editingBoard?.id === board.id ? (
                  <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                    <Input
                      value={editingBoard.name}
                      onChange={(e) => setEditingBoard({ ...editingBoard, name: e.target.value })}
                      size="sm"
                      sx={{ flex: 1 }}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <IconButton
                      size="sm"
                      color="success"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUpdateBoard(board);
                      }}
                    >
                      <Save size={16} />
                    </IconButton>
                    <IconButton
                      size="sm"
                      color="neutral"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingBoard(null);
                      }}
                    >
                      <X size={16} />
                    </IconButton>
                  </Box>
                ) : (
                  <>
                    <ListItemDecorator>
                      <ChevronRight size={16} />
                    </ListItemDecorator>
                    <ListItemContent>
                      <Typography level="body-sm">{board.name}</Typography>
                      {board.is_default && (
                        <Chip size="sm" variant="soft" color="primary">
                          Default
                        </Chip>
                      )}
                    </ListItemContent>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingBoard(board);
                        }}
                      >
                        <Edit2 size={14} />
                      </IconButton>
                      {!board.is_default && (
                        <IconButton
                          size="sm"
                          color="danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedBoard(board);
                            setShowDeleteBoardModal(true);
                          }}
                        >
                          <Trash2 size={14} />
                        </IconButton>
                      )}
                    </Box>
                  </>
                )}
              </ListItem>
            ))}
          </List>

          {boards.length === 0 && (
            <Alert color="neutral">
              No boards found. Create your first board to get started.
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Stages Management */}
      <Card variant="outlined" sx={{ flex: 1, overflow: 'auto' }}>
        <CardContent>
          {selectedBoard ? (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography level="title-md" startDecorator={<Settings size={20} />}>
                    Pipeline Stages
                  </Typography>
                  <Typography level="body-sm" color="neutral">
                    Board: {selectedBoard.name}
                  </Typography>
                </Box>
                <Button
                  size="sm"
                  startDecorator={<Plus size={16} />}
                  onClick={() => setShowNewStageModal(true)}
                >
                  Add Stage
                </Button>
              </Box>

              {loadingStages ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : stages.length > 0 ? (
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="stages">
                    {(provided) => (
                      <List
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        sx={{ gap: 1 }}
                      >
                        {stages.map((stage, index) => (
                          <Draggable
                            key={stage.id}
                            draggableId={stage.id.toString()}
                            index={index}
                          >
                            {(provided, snapshot) => (
                              <DraggablePortal snapshot={snapshot}>
                                <ListItem
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  sx={{
                                    bgcolor: snapshot.isDragging ? 'primary.softBg' : 'background.surface',
                                    borderRadius: 'sm',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    mb: 1,
                                  }}
                                >
                                  <ListItemDecorator {...provided.dragHandleProps}>
                                    <GripVertical size={16} />
                                  </ListItemDecorator>
                                
                                  {editingStage?.id === stage.id ? (
                                    <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                                      <Input
                                        value={editingStage.name}
                                        onChange={(e) => setEditingStage({ ...editingStage, name: e.target.value })}
                                        size="sm"
                                        sx={{ flex: 1 }}
                                      />
                                      <IconButton
                                        size="sm"
                                        color="success"
                                        onClick={() => handleUpdateStage(stage)}
                                      >
                                        <Save size={16} />
                                      </IconButton>
                                      <IconButton
                                        size="sm"
                                        color="neutral"
                                        onClick={() => setEditingStage(null)}
                                      >
                                        <X size={16} />
                                      </IconButton>
                                    </Box>
                                  ) : (
                                    <>
                                      <ListItemContent>
                                        <Typography level="body-sm" fontWeight="md">
                                          {stage.name}
                                        </Typography>
                                        <Typography level="body-xs" color="neutral">
                                          Position: {index + 1}
                                        </Typography>
                                      </ListItemContent>
                                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                        <Tooltip
                                          title="When enabled, opportunities in this stage add their ZIP to the owning rep's Storm Tracker watchlist. A storm hitting that ZIP auto-creates a follow-up task for the rep."
                                          placement="top"
                                        >
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 1 }}>
                                            <CloudLightning size={14} />
                                            <Switch
                                              size="sm"
                                              checked={!!stage.storm_watchlist_enabled}
                                              onChange={(e) => handleToggleStormWatchlist(stage, e.target.checked)}
                                            />
                                          </Box>
                                        </Tooltip>
                                        <IconButton
                                          size="sm"
                                          onClick={() => setEditingStage(stage)}
                                        >
                                          <Edit2 size={14} />
                                        </IconButton>
                                        <IconButton
                                          size="sm"
                                          color="danger"
                                          onClick={() => {
                                            setStageToDelete(stage);
                                            setShowDeleteStageModal(true);
                                          }}
                                        >
                                          <Trash2 size={14} />
                                        </IconButton>
                                      </Box>
                                    </>
                                  )}
                                </ListItem>
                              </DraggablePortal>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </List>
                    )}
                  </Droppable>
                </DragDropContext>
              ) : (
                <Alert color="neutral">
                  No stages defined for this board. Add stages to define your sales pipeline.
                </Alert>
              )}

              <Divider sx={{ my: 3 }} />
              
              <Alert color="primary">
                <Typography level="body-sm">
                  <strong>Tips:</strong>
                  <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                    <li>Drag and drop stages to reorder them</li>
                    <li>Click the edit icon to rename a stage</li>
                    <li>Opportunities will move through these stages in your sales pipeline</li>
                    <li>Deleting a stage with opportunities will require reassigning them</li>
                  </ul>
                </Typography>
              </Alert>
            </>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
              <Typography level="body-md" color="neutral">
                Select a board to manage its stages
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* New Board Modal */}
      <Modal open={showNewBoardModal} onClose={() => setShowNewBoardModal(false)}>
        <ModalDialog>
          <ModalClose />
          <Typography level="h4">Create New Board</Typography>
          <Box sx={{ mt: 2 }}>
            <Input
              placeholder="Board name"
              value={newBoardName}
              onChange={(e) => setNewBoardName(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button variant="outlined" onClick={() => setShowNewBoardModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateBoard}>
                Create Board
              </Button>
            </Box>
          </Box>
        </ModalDialog>
      </Modal>

      {/* New Stage Modal */}
      <Modal open={showNewStageModal} onClose={() => setShowNewStageModal(false)}>
        <ModalDialog>
          <ModalClose />
          <Typography level="h4">Add New Stage</Typography>
          <Box sx={{ mt: 2 }}>
            <Input
              placeholder="Stage name"
              value={newStageName}
              onChange={(e) => setNewStageName(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button variant="outlined" onClick={() => setShowNewStageModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateStage}>
                Add Stage
              </Button>
            </Box>
          </Box>
        </ModalDialog>
      </Modal>

      {/* Delete Board Confirmation */}
      <Modal open={showDeleteBoardModal} onClose={() => setShowDeleteBoardModal(false)}>
        <ModalDialog>
          <ModalClose />
          <Typography level="h4">Delete Board</Typography>
          <Typography level="body-md" sx={{ mt: 2 }}>
            Are you sure you want to delete the board "{selectedBoard?.name}"? 
            This action cannot be undone.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 3 }}>
            <Button variant="outlined" onClick={() => setShowDeleteBoardModal(false)}>
              Cancel
            </Button>
            <Button color="danger" onClick={handleDeleteBoard}>
              Delete Board
            </Button>
          </Box>
        </ModalDialog>
      </Modal>

      {/* Delete Stage Confirmation */}
      <Modal open={showDeleteStageModal} onClose={() => setShowDeleteStageModal(false)}>
        <ModalDialog>
          <ModalClose />
          <Typography level="h4">Delete Stage</Typography>
          <Typography level="body-md" sx={{ mt: 2 }}>
            Are you sure you want to delete the stage "{stageToDelete?.name}"? 
            Any opportunities in this stage will need to be reassigned.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 3 }}>
            <Button variant="outlined" onClick={() => setShowDeleteStageModal(false)}>
              Cancel
            </Button>
            <Button color="danger" onClick={handleDeleteStage}>
              Delete Stage
            </Button>
          </Box>
        </ModalDialog>
      </Modal>
    </Box>
  );
};

export default OpportunityBoardSettings;